import { useMemo } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Activity, Dumbbell, Flame, TrendingUp } from "lucide-react";
import type { StudentExecution } from "@/lib/types";

/**
 * Gráficos de evolução do aluno — portados de
 * `frontend/components/portal/EvolutionCharts.tsx` (legado Next). Trocamos os
 * tokens `--sp-*` pelas cores do tema novo (verde accent #2d9c5e / #1f6c4a),
 * sem qualquer indigo/roxo. Processamento de dados é imutável (map/filter).
 */

// Cores do tema (hex literais — Recharts não lê CSS vars com confiança em SVG).
const C = {
  accentSoft: "#2d9c5e",
  accent: "#1f6c4a",
  faint: "#6b6964",
  muted: "#9c9a96",
  surface: "#262624",
  borderStrong: "#44423d",
  canvas: "#1f1e1d",
  grid: "rgba(110,170,130,0.10)",
} as const;

interface EvolutionChartsProps {
  executions: StudentExecution[];
}

interface AdherenceWeekPoint {
  weekLabel: string;
  count: number;
}

interface LoadProgressPoint {
  date: string;
  shortDate: string;
  load: number;
}

interface LoadProgressByExercise {
  exerciseId: string;
  exerciseName: string;
  points: LoadProgressPoint[];
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function startOfISOWeek(date: Date): Date {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function weekLabel(date: Date): string {
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

function shortDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

/** Últimas 8 semanas de aderência (treinos completos por semana). */
function buildAdherence(executions: StudentExecution[]): AdherenceWeekPoint[] {
  const completed = executions.filter((e) => e.status === "completed");
  const buckets = new Map<number, number>();
  const now = new Date();
  const currentWeekStart = startOfISOWeek(now);

  // 8 semanas, da mais antiga pra mais nova.
  const weeks = Array.from({ length: 8 }, (_, idx) => {
    const offset = 7 - idx;
    const start = new Date(currentWeekStart);
    start.setDate(start.getDate() - offset * 7);
    buckets.set(start.getTime(), 0);
    return { start, key: start.getTime() };
  });

  for (const e of completed) {
    const ts = Date.parse(e.executed_at);
    if (!Number.isFinite(ts)) continue;
    const ws = startOfISOWeek(new Date(ts)).getTime();
    if (buckets.has(ws)) {
      buckets.set(ws, (buckets.get(ws) ?? 0) + 1);
    }
  }

  return weeks.map((w) => ({
    weekLabel: weekLabel(w.start),
    count: buckets.get(w.key) ?? 0,
  }));
}

/**
 * Progressão de carga: pega o exercício mais frequente (com carga registrada)
 * e devolve a série temporal das cargas. Limita aos últimos 10 pontos pra UX.
 */
function buildLoadProgression(
  executions: StudentExecution[],
): LoadProgressByExercise | null {
  const completed = executions.filter((e) => e.status === "completed");
  if (completed.length === 0) return null;

  const exerciseMap = new Map<
    string,
    { name: string; entries: { ts: number; load: number; date: string }[] }
  >();

  for (const exec of completed) {
    for (const it of exec.items) {
      if (it.load_kg == null || !it.completed_at) continue;
      const key = it.workout_item_id;
      const entry = exerciseMap.get(key) ?? { name: it.exercise_name, entries: [] };
      entry.entries.push({
        ts: Date.parse(exec.executed_at),
        load: it.load_kg,
        date: exec.executed_at,
      });
      exerciseMap.set(key, entry);
    }
  }

  if (exerciseMap.size === 0) return null;

  // Pega o exercício com mais pontos.
  let bestKey: string | null = null;
  let bestCount = 0;
  for (const [k, v] of exerciseMap.entries()) {
    if (v.entries.length > bestCount) {
      bestKey = k;
      bestCount = v.entries.length;
    }
  }
  if (!bestKey || bestCount < 2) return null;

  const best = exerciseMap.get(bestKey)!;
  const sorted = [...best.entries].sort((a, b) => a.ts - b.ts).slice(-10);

  return {
    exerciseId: bestKey,
    exerciseName: best.name,
    points: sorted.map((e) => ({
      date: e.date,
      shortDate: shortDay(e.date),
      load: e.load,
    })),
  };
}

interface StatTotals {
  total: number;
  weeklyAvg: number;
  thisWeek: number;
  rpeAvg: number | null;
}

function buildTotals(executions: StudentExecution[]): StatTotals {
  const completed = executions.filter((e) => e.status === "completed");
  const total = completed.length;

  let weeklyAvg = 0;
  if (completed.length > 0) {
    const dates = completed.map((e) => Date.parse(e.executed_at)).filter(Number.isFinite);
    if (dates.length > 0) {
      const range = (Math.max(...dates) - Math.min(...dates)) / (1000 * 60 * 60 * 24);
      const weeks = Math.max(1, range / 7);
      weeklyAvg = total / weeks;
    }
  }

  const now = Date.now();
  const oneWeek = 7 * 24 * 60 * 60 * 1000;
  const thisWeek = completed.filter(
    (e) => now - Date.parse(e.executed_at) <= oneWeek,
  ).length;

  const rpes = completed.flatMap((e) =>
    e.items
      .map((it) => it.rpe)
      .filter((r): r is number => typeof r === "number" && Number.isFinite(r)),
  );
  const rpeAvg = rpes.length > 0 ? rpes.reduce((a, b) => a + b, 0) / rpes.length : null;

  return { total, weeklyAvg, thisWeek, rpeAvg };
}

// ─── Componente ─────────────────────────────────────────────────────────────

export function EvolutionCharts({ executions }: EvolutionChartsProps) {
  const totals = useMemo(() => buildTotals(executions), [executions]);
  const adherence = useMemo(() => buildAdherence(executions), [executions]);
  const loadProgression = useMemo(
    () => buildLoadProgression(executions),
    [executions],
  );

  const maxAdherence = useMemo(
    () => adherence.reduce((m, p) => Math.max(m, p.count), 0),
    [adherence],
  );

  return (
    <div className="space-y-4">
      {/* Stats grid: 2x2 mobile, 4 col desktop */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        <StatTile
          icon={Dumbbell}
          label="Treinos"
          value={totals.total.toString()}
          accent="emerald"
        />
        <StatTile
          icon={Flame}
          label="Esta semana"
          value={totals.thisWeek.toString()}
          accent="emerald"
        />
        <StatTile
          icon={TrendingUp}
          label="Média/semana"
          value={totals.weeklyAvg > 0 ? totals.weeklyAvg.toFixed(1) : "—"}
          accent="neutral"
        />
        <StatTile
          icon={Activity}
          label="RPE médio"
          value={totals.rpeAvg != null ? totals.rpeAvg.toFixed(1) : "—"}
          accent="neutral"
        />
      </div>

      {/* Aderência semanal — BarChart (treinos completos por semana) */}
      <section className="rounded-3xl border border-border-subtle bg-surface p-4 sm:p-5">
        <header className="mb-3 flex items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
              Aderência
            </p>
            <h3 className="font-display text-base text-ink">Últimas 8 semanas</h3>
          </div>
          <span className="text-[11px] text-muted">treinos/semana</span>
        </header>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={adherence} margin={{ top: 5, right: 6, left: -28, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 3" stroke={C.grid} vertical={false} />
              <XAxis
                dataKey="weekLabel"
                stroke={C.faint}
                fontSize={10}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke={C.faint}
                fontSize={10}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={28}
              />
              <Tooltip
                cursor={{ fill: "rgba(110,170,130,0.06)" }}
                contentStyle={{
                  background: C.surface,
                  border: `1px solid ${C.borderStrong}`,
                  borderRadius: "10px",
                  fontSize: "12px",
                }}
                labelStyle={{ color: C.muted }}
                itemStyle={{ color: C.accentSoft }}
                formatter={(value) => [`${value} treinos`, "Aderência"]}
              />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={28}>
                {adherence.map((p, i) => (
                  <Cell
                    key={i}
                    fill={p.count >= maxAdherence && maxAdherence > 0 ? C.accentSoft : C.accent}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Progressão de carga — LineChart */}
      <section className="rounded-3xl border border-border-subtle bg-surface p-4 sm:p-5">
        <header className="mb-3 flex items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
              Carga progressiva
            </p>
            <h3 className="truncate font-display text-base text-ink">
              {loadProgression?.exerciseName ?? "Progressão de carga"}
            </h3>
          </div>
          {loadProgression ? <span className="text-[11px] text-muted">kg</span> : null}
        </header>
        {loadProgression ? (
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={loadProgression.points}
                margin={{ top: 5, right: 6, left: -28, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="portalLoadFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.accentSoft} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={C.accentSoft} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 3" stroke={C.grid} vertical={false} />
                <XAxis
                  dataKey="shortDate"
                  stroke={C.faint}
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke={C.faint}
                  fontSize={10}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                  width={28}
                />
                <Tooltip
                  contentStyle={{
                    background: C.surface,
                    border: `1px solid ${C.borderStrong}`,
                    borderRadius: "10px",
                    fontSize: "12px",
                  }}
                  labelStyle={{ color: C.muted }}
                  itemStyle={{ color: C.accentSoft }}
                  formatter={(value) => [`${value} kg`, "Carga"]}
                />
                <Line
                  type="monotone"
                  dataKey="load"
                  stroke={C.accentSoft}
                  strokeWidth={2.5}
                  dot={{ fill: C.accentSoft, r: 4, strokeWidth: 0 }}
                  activeDot={{ r: 6, stroke: C.canvas, strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex h-44 items-center justify-center rounded-2xl border border-dashed border-border-subtle bg-elevated/40">
            <div className="text-center">
              <p className="text-sm text-muted">Registre cargas em pelo menos 2 treinos</p>
              <p className="mt-1 text-xs text-faint">pra ver a progressão do exercício</p>
            </div>
          </div>
        )}
      </section>

      {/* Tendência (área) — mantida pra leitura rápida da curva de aderência */}
      <section className="rounded-3xl border border-border-subtle bg-surface p-4 sm:p-5">
        <header className="mb-3 flex items-end justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-faint">
              Tendência
            </p>
            <h3 className="font-display text-base text-ink">Curva de aderência</h3>
          </div>
        </header>
        <div className="h-36">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={adherence} margin={{ top: 5, right: 6, left: -28, bottom: 0 }}>
              <defs>
                <linearGradient id="portalAdherenceFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.accentSoft} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={C.accentSoft} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 3" stroke={C.grid} vertical={false} />
              <XAxis
                dataKey="weekLabel"
                stroke={C.faint}
                fontSize={10}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke={C.faint}
                fontSize={10}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={28}
              />
              <Tooltip
                contentStyle={{
                  background: C.surface,
                  border: `1px solid ${C.borderStrong}`,
                  borderRadius: "10px",
                  fontSize: "12px",
                }}
                labelStyle={{ color: C.muted }}
                itemStyle={{ color: C.accentSoft }}
                formatter={(value) => [`${value} treinos`, "Aderência"]}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke={C.accentSoft}
                strokeWidth={2}
                fill="url(#portalAdherenceFill)"
                dot={{ fill: C.accentSoft, r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5, stroke: C.canvas, strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}

// ─── StatTile ────────────────────────────────────────────────────────────────

interface StatTileProps {
  icon: typeof Dumbbell;
  label: string;
  value: string;
  accent: "emerald" | "neutral";
}

function StatTile({ icon: Icon, label, value, accent }: StatTileProps) {
  const iconColor = accent === "emerald" ? "text-accent-soft" : "text-muted";
  return (
    <div className="rounded-2xl border border-border-subtle bg-surface p-3">
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className={`flex h-7 w-7 items-center justify-center rounded-lg bg-elevated ${iconColor}`}
        >
          <Icon size={14} />
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-faint">
          {label}
        </span>
      </div>
      <div className="mt-1.5 font-display text-xl text-ink">{value}</div>
    </div>
  );
}
